import { useCallback, useEffect, useState } from 'react'

import {
  clearDismissed,
  readDismissed,
  shouldShowBanner,
  writeDismissed,
} from '../components/twilio-balance/bannerVisibility'
import type { TwilioBalanceState, TwilioBalanceStatus } from '../services/twilioBalance'
import { useTwilioBalance } from './useTwilioBalance'

export interface TwilioBalanceBannerModel {
  status: TwilioBalanceStatus | undefined
  /** true ⇔ l'AppShell doit RENDRE le bandeau (décision prise ici, pas dans le composant — garde H9). */
  visible: boolean
  /** Ferme le bandeau pour la session, au palier courant. */
  dismiss: () => void
}

/**
 * LOT #142 / E-20 — modèle du bandeau de solde Twilio pour l'AppShell.
 *
 * Compose la requête (`useTwilioBalance`) et la mémoire de fermeture
 * (`sessionStorage`, via `bannerVisibility`) :
 *   - fermé au palier P, le bandeau ne revient que si l'état devient plus
 *     grave que P ;
 *   - un retour à OK/UNKNOWN EFFACE la fermeture : le prochain épisode
 *     (nouvelle baisse de solde) alerte à nouveau — sinon un CRITICAL fermé
 *     un lundi rendrait muet le CRITICAL du jeudi.
 */
export function useTwilioBalanceBanner(enabled: boolean, isSuperAdmin: boolean): TwilioBalanceBannerModel {
  const { data: status } = useTwilioBalance(enabled)
  const [dismissed, setDismissed] = useState<TwilioBalanceState | null>(() => readDismissed())

  const state = status?.state

  useEffect(() => {
    if (!dismissed) return
    if (state === 'OK' || state === 'UNKNOWN') {
      clearDismissed()
      setDismissed(null)
    }
  }, [state, dismissed])

  const dismiss = useCallback(() => {
    if (!state) return
    writeDismissed(state)
    setDismissed(state)
  }, [state])

  return {
    status,
    visible: shouldShowBanner(state, isSuperAdmin, dismissed),
    dismiss,
  }
}
